import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import productApi from '@/lib/productApi';
import { inlineError, type InlineError } from '@/lib/errorReport';
import { toast } from '@/lib/toast';
import type { RateView } from '@/types/product';
import RateCombobox from './RateCombobox';
import { money } from './workbenchPricing';

interface Props {
  rate: RateView | null;
  rates: RateView[];
  onClose: () => void;
  onRemoved: () => void | Promise<void>;
}
const errorMessage = (error: unknown) =>
  (error as { response?: { data?: { error?: string } } }).response?.data?.error ??
  'Het tarief kon niet worden verwijderd. Probeer opnieuw.';

export default function RateRemovalDialog({ rate, rates, onClose, onRemoved }: Props) {
  const [replacement, setReplacement] = useState('');
  const [impact, setImpact] = useState<Awaited<
    ReturnType<typeof productApi.previewRateRemoval>
  > | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<InlineError | null>(null);
  const [retry, setRetry] = useState(0);
  const choices = rates.filter(
    (item) =>
      item.id !== rate?.id &&
      item.currentCents !== null &&
      (rate?.hubspotPortalId != null || item.hubspotPortalId == null)
  );
  const replacementId = choices.find((item) => item.key === replacement)?.id;
  useEffect(() => {
    setReplacement('');
    setImpact(null);
    setError(null);
  }, [rate?.id]);
  useEffect(() => {
    if (!rate) return;
    let active = true;
    setLoading(true);
    setError(null);
    setImpact(null);
    productApi
      .previewRateRemoval(rate.id, replacementId)
      .then((data) => {
        if (active) setImpact(data);
      })
      .catch((err) => {
        if (active) setError(inlineError(errorMessage(err), err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [rate, replacementId, retry]);
  const apply = async () => {
    if (!rate || !replacementId) return;
    setBusy(true);
    try {
      await productApi.removeRate(rate.id, replacementId);
      await onRemoved();
      onClose();
      toast.success('Tarief verwijderd en productregels gekoppeld aan het vervangende tarief');
    } catch (err) {
      setError(inlineError(errorMessage(err), err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={Boolean(rate)}
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tarief verwijderen</DialogTitle>
        </DialogHeader>
        <p className="text-sm">
          Je verwijdert <strong>{rate?.name}</strong>. Kies een ander tarief voor alle gekoppelde
          productregels. Bestaande offertes behouden hun opgeslagen prijzen.
        </p>
        <div className="space-y-2">
          <p className="text-sm font-medium">Vervangend tarief</p>
          <RateCombobox
            label="Vervangend tarief"
            value={replacement}
            rates={choices}
            ratesByKey={Object.fromEntries(choices.map((item) => [item.key, item.currentCents!]))}
            disabled={busy}
            onChange={setReplacement}
          />
          {!choices.length && (
            <p className="text-sm text-muted-foreground">
              Voeg eerst een ander tarief toe via Tariefbeheer.
            </p>
          )}
        </div>
        {loading && (
          <p role="status" className="text-sm text-muted-foreground">
            Gekoppelde producten controleren…
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error.message} <ErrorReportButton report={error.report} />
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setRetry((value) => value + 1)}
            >
              Opnieuw controleren
            </Button>
          </p>
        )}
        {impact && (
          <div className="space-y-2 text-sm">
            <p>
              {impact.products.length}{' '}
              {impact.products.length === 1 ? 'product wordt' : 'producten worden'} gekoppeld aan
              het nieuwe tarief.
            </p>
            <ul className="max-h-56 space-y-3 overflow-y-auto">
              {impact.products.map((product) => (
                <li key={product.id} className="border-t pt-2">
                  <p className="font-medium">{product.name}</p>
                  {replacementId &&
                    product.variants.map((variant, index) => (
                      <p key={index} className="text-xs text-muted-foreground">
                        {variant.name}: {money(variant.fromCents)} → {money(variant.toCents)}
                      </p>
                    ))}
                  {product.errors.map((message, index) => (
                    <p key={index} role="alert" className="text-xs text-destructive">
                      {message}
                    </p>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Annuleren
          </Button>
          <Button
            variant="destructive"
            disabled={
              busy ||
              loading ||
              !replacementId ||
              !impact ||
              Boolean(error) ||
              impact.products.some((product) => product.errors.length)
            }
            onClick={apply}
          >
            {busy ? 'Bezig…' : 'Verwijderen en vervangen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
